import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Drawer,
  Box,
  ButtonBase,
  Typography,
  ListItemIcon,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import MainLayout from '../Common/MainLayout';
import DocumentViewer from './DocumentViewer'; // Import DocumentViewer
import axios from 'axios';
import { useAuthState } from '../../Context';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';

function Documents() {
  const { session } = useAuthState();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const navigate = useNavigate();

  const fetchDocuments = async () => {
    const token = session.getIdToken().getJwtToken();
    const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/getDocuments`;
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          authorizationToken: token,
        },
      });
      if (response.status !== 200) {
        throw new Error('Failed to fetch documents');
      }
      return Array.isArray(response.data)
        ? response.data.map((doc) => ({
            id: doc.view_url,
            title: doc.title,
            content: doc.document,
            group: doc.doc_group,
          }))
        : [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  };

  const {
    data: documents,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['DocumentsData', session.getIdToken().getJwtToken()],
    queryFn: fetchDocuments,
    refetchOnWindowFocus: false,
  });

  const groupedDocuments = documents?.reduce((acc, doc) => {
    if (!acc[doc.group]) {
      acc[doc.group] = [];
    }
    acc[doc.group].push(doc);
    return acc;
  }, {});

  useEffect(() => {
    if (!selectedDocument && groupedDocuments) {
      const firstGroup = Object.keys(groupedDocuments).sort()[0];
      if (firstGroup && groupedDocuments[firstGroup].length > 0) {
        setSelectedDocument(groupedDocuments[firstGroup][0]);
      }
    }
  }, [groupedDocuments, selectedDocument]);

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
    navigate('', { replace: true });
  };

  return (
    <MainLayout>
      {isLoading || isError ? (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {isLoading ? (
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="h8" component="p" color="primary">
                Loading Documents...
              </Typography>
              <CircularProgress />
            </Grid>
          ) : (
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Error: {error.message}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <Drawer
            variant="permanent"
            sx={{
              width: 240,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 240,
                boxSizing: 'border-box',
                position: 'relative',
              },
            }}
          >
            <Box
              sx={{
                overflow: 'hidden',
                m: 2,
              }}
            >
              <List>
                {groupedDocuments &&
                  Object.keys(groupedDocuments)
                    .sort()
                    .map((group) => (
                      <React.Fragment key={group}>
                        <ListItem>
                          <ListItemText
                            primary={group}
                            primaryTypographyProps={{
                              fontWeight: 'bold',
                              fontSize: '1rem',
                            }}
                          />
                        </ListItem>
                        {groupedDocuments[group].map((doc) => (
                          <ButtonBase
                            key={doc.id}
                            onClick={() => handleDocumentSelect(doc)}
                            sx={{
                              width: '100%',
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                              bgcolor:
                                selectedDocument && selectedDocument.id === doc.id
                                  ? 'secondary.main'
                                  : 'transparent',
                              '&:hover': {
                                bgcolor: 'secondary.light',
                                transform: 'scale(1.02)',
                              },
                              transition: 'transform .2s, background-color .2s',
                              cursor: 'pointer',
                            }}
                          >
                            <ListItem
                              sx={{
                                padding: '4px 8px',
                                width: '100%',
                                minHeight: 32,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                <DescriptionIcon sx={{ fontSize: '1rem' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={doc.title}
                                primaryTypographyProps={{
                                  fontWeight:
                                    selectedDocument && selectedDocument.id === doc.id
                                      ? 'bold'
                                      : 'fontWeightMedium',
                                  fontSize: '0.875rem',
                                }}
                              />
                            </ListItem>
                          </ButtonBase>
                        ))}
                      </React.Fragment>
                    ))}
              </List>
            </Box>
          </Drawer>
          {selectedDocument ? (
            <DocumentViewer title={selectedDocument.title} content={selectedDocument.content} />
          ) : (
            <Box
              component="section"
              sx={{ flexGrow: 1, overflow: 'auto', padding: 2 }}
            >
              <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
                Currently no documents available
              </Typography>
            </Box>
          )}
        </>
      )}
    </MainLayout>
  );
}

export default Documents;
